import { useState } from "react";
import { motion } from "framer-motion";
import { STEPS } from "../lib/site";
import { Bars, AreaSpark } from "./charts";

const Visual = ({ step }) => {
    switch (step.viz) {
        case "baseline":
            return (
                <div className="w-full max-w-sm">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Baseline metrics</p>
                    <div className="mt-4 rounded-xl border border-ink/10 bg-white p-6">
                        <Bars dark={false} height={140} items={[{ n: "PIPELINE", v: 60, c: "rgba(57,119,246,0.7)" }, { n: "CONVERSION", v: 34, c: "rgba(30,136,247,0.7)" }, { n: "VELOCITY", v: 45, c: "rgba(11,18,32,0.6)" }, { n: "GROSS PROFIT", v: 52, c: "rgba(30,136,247,0.5)" }]} />
                    </div>
                </div>
            );
        case "matrix":
            return (
                <div className="w-full max-w-sm">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Impact × Speed × Confidence × Effort</p>
                    <div className="mt-4 grid grid-cols-3 gap-1.5 rounded-xl border border-ink/10 bg-white p-6">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <motion.div key={i} initial={{ opacity: 0, scale: 0.7 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className={`flex aspect-square items-center justify-center rounded-md font-mono text-xs ${i === 1 ? "bg-vantera text-ink" : i === 4 ? "bg-azure-soft text-azure" : "bg-ink/[0.04] text-ink/30"}`}>
                                {i === 1 ? "P1" : i === 4 ? "P2" : "·"}
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
        case "deploy":
            return (
                <div className="w-full max-w-sm">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">System deployment</p>
                    <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl border border-ink/10 bg-white p-6">
                        {["ACCOUNT INTELLIGENCE", "AI FOLLOW-UP", "REVENUE RECOVERY", "AD OPTIMIZATION", "SALES ENABLEMENT", "COST REVIEW"].map((n, i) => (
                            <motion.div key={n} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="rounded-md border border-ink/10 bg-mist px-3 py-3 font-mono text-[9px] uppercase tracking-[0.14em] text-slategray">
                                {n}
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
        case "measure":
            return (
                <div className="w-full max-w-sm">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Measured against outcomes</p>
                    <div className="mt-4 rounded-xl border border-ink/10 bg-white p-6">
                        <AreaSpark dark={false} series={[20, 26, 24, 32, 38, 36, 46, 52]} color="#1E88F7" id="process-measure" height={140} />
                    </div>
                </div>
            );
        default:
            return (
                <div className="w-full max-w-sm">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Full-economic examination</p>
                    <div className="scan-sweep relative mt-4 rounded-xl border border-ink/10 bg-white p-6">
                        {["REVENUE", "SALES", "MARKETING", "CUSTOMERS", "CRM", "MARGIN", "VENDORS", "AUTOMATION"].map((n, i) => (
                            <motion.div key={n} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className="flex items-center justify-between border-b border-ink/[0.06] py-2.5 last:border-0">
                                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slategray">{n}</span>
                                <span className="font-mono text-[10px] text-vantera">✓ scanned</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
    }
};

const ProcessScroll = () => {
    const [active, setActive] = useState(0);

    return (
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20" data-testid="process-scroll">
            <div className="hidden lg:block">
                <div className="sticky top-28">
                    <p className="text-outline pointer-events-none select-none font-display text-[190px] font-extrabold leading-none" aria-hidden="true">
                        {STEPS[active].num}
                    </p>
                    <div className="mt-6">
                        <Visual step={STEPS[active]} />
                    </div>
                </div>
            </div>

            <div className="space-y-5">
                {STEPS.map((s, i) => (
                    <motion.div
                        key={s.num}
                        onViewportEnter={() => setActive(i)}
                        viewport={{ margin: "-42% 0px -42% 0px" }}
                        initial={{ opacity: 0.35 }}
                        animate={{ opacity: active === i ? 1 : 0.35 }}
                        transition={{ duration: 0.4 }}
                        className={`relative rounded-2xl border p-7 sm:p-9 transition-colors duration-500 ${active === i ? "border-vantera/40 bg-white shadow-[0_24px_60px_-30px_rgba(11,18,32,0.25)]" : "border-ink/10 bg-transparent"}`}
                        data-testid={`process-step-${i + 1}`}
                    >
                        <div className="flex items-center gap-4">
                            <span className={`font-mono text-xs tracking-[0.2em] ${active === i ? "text-vantera" : "text-slategray"}`}>{s.num}</span>
                            <span className="h-px w-8 bg-ink/15" aria-hidden="true" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slategray">{s.title}</span>
                        </div>
                        <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{s.name}</h3>
                        <p className="mt-4 max-w-xl text-[17px] leading-relaxed text-slategray">{s.desc}</p>
                        <div className="mt-6 lg:hidden">
                            <Visual step={s} />
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default ProcessScroll;